import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { ensureStyles } from "../styles.js";
import { ChatView } from "./ChatView.js";
import { useAgents } from "../hooks.js";
export function ChatBubble({ baseUrl, getAuthToken, preferredAgent, defaultAgent, debug = false, panelTitle = "Copilot", panelWidth = 420, defaultOpen = false, hidden = false, starterQuestions: starterQuestionsProp, dismissable = false, }) {
    const [open, setOpen] = useState(defaultOpen);
    const [hasOpened, setHasOpened] = useState(defaultOpen);
    const [fullScreen, setFullScreen] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [suggestedPrompt, setSuggestedPrompt] = useState();
    const suggestedPromptIdRef = useRef(0);
    const [dismissed, setDismissed] = useState(false);
    const { agents, error: agentsError } = useAgents(baseUrl, getAuthToken, !hidden);
    useEffect(() => {
        ensureStyles();
    }, []);
    // Escape key closes the panel
    useEffect(() => {
        if (!open)
            return;
        const handleKey = (e) => {
            if (e.key === "Escape") {
                setOpen(false);
                setFullScreen(false);
            }
        };
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [open]);
    const suggestedAgent = useMemo(() => (preferredAgent
        ? agents.find((a) => a.name === preferredAgent)
        : undefined)
        ?? (defaultAgent
            ? agents.find((a) => a.name === defaultAgent)
            : undefined)
        ?? agents[0], [agents, preferredAgent, defaultAgent]);
    const starterQuestions = starterQuestionsProp ?? suggestedAgent?.starterQuestions ?? [];
    /* ── Peek: briefly flash suggestions when questions change ── */
    const [peekVisible, setPeekVisible] = useState(false);
    const peekTimerRef = useRef(null);
    const lastPeekedKeyRef = useRef("");
    const questionsKey = starterQuestions.join("\n");
    useEffect(() => {
        if (open || hidden || starterQuestions.length === 0)
            return;
        if (lastPeekedKeyRef.current === questionsKey)
            return;
        lastPeekedKeyRef.current = questionsKey;
        setPeekVisible(true);
        const timer = setTimeout(() => {
            setPeekVisible(false);
        }, 4000);
        peekTimerRef.current = timer;
        return () => clearTimeout(timer);
    }, [questionsKey, open, hidden, starterQuestions.length]);
    // If user hovers while peeking, cancel the timer (hover takes over)
    useEffect(() => {
        if (hovered && peekTimerRef.current) {
            clearTimeout(peekTimerRef.current);
            peekTimerRef.current = null;
            setPeekVisible(false);
        }
    }, [hovered]);
    const showSuggestions = !open && !hidden && (hovered || peekVisible) && starterQuestions.length > 0;
    if (agentsError || !suggestedAgent)
        return null;
    return (_jsxs("div", { className: "copilot-agents-root copilot-agents-bubble-root", style: hidden || dismissed ? { display: 'none' } : undefined, children: [_jsxs("div", { className: "copilot-agents-bubble-shell", onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [showSuggestions && (_jsxs("div", { className: "copilot-agents-suggestions", children: [_jsxs("div", { className: "copilot-agents-suggestions-title", children: ["Try asking ", suggestedAgent?.name || "an", " agent"] }), _jsx("div", { className: "copilot-agents-suggestions-list", children: starterQuestions.map((question) => (_jsx("button", { className: "copilot-agents-suggestion", type: "button", onClick: () => {
                                        setSuggestedPrompt({
                                            id: ++suggestedPromptIdRef.current,
                                            agent: suggestedAgent.name,
                                            message: question,
                                        });
                                        setHasOpened(true);
                                        setOpen(true);
                                    }, children: question }, question))) })] })), _jsxs("div", { className: "copilot-agents-bubble-button-wrapper", children: [dismissable && !open && (_jsx("button", { className: "copilot-agents-bubble-dismiss", type: "button", onClick: (e) => {
                                    e.stopPropagation();
                                    setDismissed(true);
                                }, "aria-label": "Dismiss Copilot bubble", children: "\u00D7" })), _jsx("button", { className: "copilot-agents-bubble-button", type: "button", onClick: () => {
                                    setOpen((prev) => {
                                        const next = !prev;
                                        if (!next)
                                            setFullScreen(false);
                                        if (next)
                                            setHasOpened(true);
                                        return next;
                                    });
                                }, "aria-label": open ? "Close Copilot panel" : "Open Copilot panel", children: open ? "×" : "💬" })] })] }), hasOpened && (_jsxs(_Fragment, { children: [_jsx("div", { className: `copilot-agents-backdrop${open ? "" : " copilot-agents-backdrop--hidden"}`, onClick: () => {
                            setOpen(false);
                            setFullScreen(false);
                        }, role: "presentation" }), _jsxs("aside", { className: `copilot-agents-panel${fullScreen ? " copilot-agents-panel--fullscreen" : ""}${open ? "" : " copilot-agents-panel--hidden"}`, style: fullScreen ? undefined : { width: panelWidth }, children: [_jsxs("header", { className: "copilot-agents-panel-header", children: [_jsx("div", { className: "copilot-agents-panel-title", children: panelTitle }), _jsxs("div", { className: "copilot-agents-panel-actions", children: [_jsx("button", { className: "copilot-agents-panel-action", type: "button", onClick: () => setFullScreen((prev) => !prev), "aria-label": fullScreen ? "Exit full screen" : "Enter full screen", children: fullScreen ? "🗗" : "⛶" }), _jsx("button", { className: "copilot-agents-panel-close", type: "button", onClick: () => {
                                                    setOpen(false);
                                                    setFullScreen(false);
                                                }, "aria-label": "Close Copilot panel", children: "\u00D7" })] })] }), _jsx("div", { className: "copilot-agents-panel-body", children: _jsx(ChatView, { baseUrl: baseUrl, getAuthToken: getAuthToken, preferredAgent: preferredAgent, defaultAgent: defaultAgent, debug: debug, starterQuestions: starterQuestionsProp, initialMessage: suggestedPrompt?.message, initialAgent: suggestedPrompt?.agent, initialMessageKey: suggestedPrompt?.id, onInitialMessageConsumed: () => setSuggestedPrompt(undefined) }) })] })] }))] }));
}
//# sourceMappingURL=ChatBubble.js.map